import { useEffect, useId, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  BadgeCheck,
  Camera,
  CircleCheck,
  Clock,
  Loader2,
  Package,
  Send,
  X,
  XCircle,
  type LucideIcon,
} from 'lucide-react'

import {
  fileWarrantyClaim,
  portalProjectQueryOptions,
  type PortalItem,
  type PortalOrder,
  type PortalProject,
  type PortalWarrantyClaim,
  type PortalWarrantyClaimStatus,
} from '@/api/client'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { claimableItems, formatFullDay } from '@/lib/tracker'
import { cn } from '@/lib/utils'

const MAX_PHOTOS = 8

const STATUS: Record<PortalWarrantyClaimStatus, { label: string; icon: LucideIcon; tone: string }> = {
  submitted: { label: 'Received', icon: Clock, tone: 'text-foreground' },
  with_manufacturer: { label: 'With the manufacturer', icon: Send, tone: 'text-usa-blue' },
  approved: { label: 'Approved', icon: CircleCheck, tone: 'text-emerald-700' },
  resolved: { label: 'Resolved', icon: BadgeCheck, tone: 'text-emerald-700' },
  declined: { label: 'Not covered', icon: XCircle, tone: 'text-rose-700' },
}

/** The order's warranty claims and the way to file one, once something is delivered (USA-915). */
export function WarrantyClaims({ token, order }: { token: string; order: PortalOrder }) {
  const headingId = useId()
  const [filing, setFiling] = useState(false)
  const items = claimableItems(order)
  if (items.length === 0 && order.warranty_claims.length === 0) return null
  return (
    <section aria-labelledby={headingId}>
      <div className="flex items-center justify-between gap-4">
        <h2 id={headingId} className="text-xl font-semibold tracking-tight">
          Warranty claims
        </h2>
        {items.length > 0 ? (
          <Button variant="outline" onClick={() => setFiling(true)}>
            File a claim
          </Button>
        ) : null}
      </div>
      {order.warranty_claims.length > 0 ? (
        <ul className="bg-card mt-4 divide-y overflow-hidden rounded-2xl border">
          {order.warranty_claims.map((claim) => (
            <ClaimRow key={claim.id} claim={claim} />
          ))}
        </ul>
      ) : null}
      <Sheet open={filing} onOpenChange={setFiling}>
        <SheetContent aria-describedby={undefined}>
          {filing ? (
            <ClaimForm token={token} items={items} onFiled={() => setFiling(false)} />
          ) : null}
        </SheetContent>
      </Sheet>
    </section>
  )
}

function ClaimRow({ claim }: { claim: PortalWarrantyClaim }) {
  const status = STATUS[claim.status]
  const Icon = status.icon
  const meta = [
    `Claim ${claim.number}`,
    `Filed ${formatFullDay(claim.submitted_on)}`,
    claim.manufacturer_claim_number ? `Manufacturer #${claim.manufacturer_claim_number}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <li className="grid grid-cols-[3.5rem_minmax(0,1fr)] items-start gap-4 px-4 py-4 sm:px-5">
      <ItemImage url={claim.image_url} />
      <div className="min-w-0">
        <p className="line-clamp-2 text-sm leading-snug font-medium">{claim.name}</p>
        <p className="text-muted-foreground mt-0.5 text-xs">{meta}</p>
        <p className={cn('mt-3 inline-flex items-center gap-1.5 text-sm font-semibold', status.tone)}>
          <Icon aria-hidden="true" className="size-[18px]" />
          {status.label}
        </p>
        {claim.note ? <p className="mt-1 text-sm text-pretty">{claim.note}</p> : null}
      </div>
    </li>
  )
}

function ClaimForm({
  token,
  items,
  onFiled,
}: {
  token: string
  items: PortalItem[]
  onFiled: () => void
}) {
  const id = useId()
  const queryClient = useQueryClient()
  const queryKey = portalProjectQueryOptions(token).queryKey
  const [lineId, setLineId] = useState<number | null>(items.length === 1 ? (items[0]?.line_id ?? null) : null)
  const [problem, setProblem] = useState('')
  const [unitDown, setUnitDown] = useState<boolean | null>(null)
  const [photos, setPhotos] = useState<File[]>([])
  const [serial, setSerial] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const file = useMutation({
    mutationFn: () =>
      fileWarrantyClaim(token, {
        lineId: lineId!,
        problem: problem.trim(),
        unitDown: unitDown!,
        serial: serial.trim(),
        contactName: name.trim(),
        contactEmail: email.trim(),
        contactPhone: phone.trim(),
        photos,
      }),
    onSuccess: (claim) => {
      queryClient.setQueryData<PortalProject>(queryKey, (project) =>
        project
          ? {
              ...project,
              orders: project.orders.map((order) =>
                order.deliveries.some((d) => d.items.some((item) => item.line_id === claim.line_id))
                  ? { ...order, warranty_claims: [claim, ...order.warranty_claims] }
                  : order,
              ),
            }
          : project,
      )
      void queryClient.invalidateQueries({ queryKey })
      onFiled()
    },
  })
  const ready =
    lineId !== null &&
    problem.trim().length >= 10 &&
    unitDown !== null &&
    photos.length > 0 &&
    name.trim() !== '' &&
    phone.trim() !== '' &&
    email.trim() !== ''

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(event) => {
        event.preventDefault()
        if (ready && !file.isPending) file.mutate()
      }}
    >
      <div className="border-b px-5 pt-4 pb-3">
        <SheetTitle className="pr-10">File a warranty claim</SheetTitle>
      </div>
      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain px-5 pt-5 pb-6">

        {items.length > 1 ? (
          <fieldset>
            <legend className="text-sm font-medium">Which item?</legend>
            <div className="mt-2 grid gap-2">
              {items.map((item) => (
                <label
                  key={item.line_id}
                  className={cn(
                    'grid cursor-pointer grid-cols-[2.75rem_minmax(0,1fr)] items-center gap-3 rounded-xl border p-2.5',
                    'has-[:focus-visible]:ring-ring/50 has-[:focus-visible]:ring-[3px]',
                    lineId === item.line_id ? 'border-usa-blue bg-usa-blue/5' : 'hover:bg-muted/60',
                  )}
                >
                  <input
                    type="radio"
                    name={`${id}-item`}
                    className="sr-only"
                    checked={lineId === item.line_id}
                    onChange={() => setLineId(item.line_id)}
                  />
                  <ItemImage url={item.image_url} small />
                  <span className="min-w-0">
                    <span className="line-clamp-2 text-sm leading-snug font-medium">{item.name}</span>
                    {item.model ? (
                      <span className="text-muted-foreground block font-mono text-xs">{item.model}</span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ) : items[0] ? (
          <div className="grid grid-cols-[2.75rem_minmax(0,1fr)] items-center gap-3">
            <ItemImage url={items[0].image_url} small />
            <p className="line-clamp-2 text-sm leading-snug font-medium">{items[0].name}</p>
          </div>
        ) : null}

        <Field id={`${id}-problem`} label="What's wrong?">
          <textarea
            id={`${id}-problem`}
            value={problem}
            onChange={(event) => setProblem(event.target.value)}
            rows={4}
            maxLength={4000}
            className={cn(INPUT, 'h-auto min-h-28 resize-y py-2.5 leading-relaxed')}
          />
        </Field>

        <fieldset>
          <legend className="text-sm font-medium">Is it working?</legend>
          <div className="bg-muted mt-2 grid grid-cols-2 gap-1 rounded-xl p-1">
            {[
              { value: true, label: 'Not working' },
              { value: false, label: 'Working, with a problem' },
            ].map((option) => (
              <label
                key={option.label}
                className={cn(
                  'grid min-h-11 cursor-pointer place-items-center rounded-lg px-2 py-2 text-center text-sm leading-tight font-medium',
                  'has-[:focus-visible]:ring-ring/50 has-[:focus-visible]:ring-[3px]',
                  unitDown === option.value
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <input
                  type="radio"
                  name={`${id}-working`}
                  className="sr-only"
                  checked={unitDown === option.value}
                  onChange={() => setUnitDown(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>

        <PhotoField id={`${id}-photos`} photos={photos} onChange={setPhotos} />

        <Field id={`${id}-serial`} label="Serial number" optional>
          <input
            id={`${id}-serial`}
            value={serial}
            onChange={(event) => setSerial(event.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={80}
            className={cn(INPUT, 'font-mono uppercase')}
          />
        </Field>

        <fieldset className="space-y-4">
          <legend className="text-sm font-medium">Who should the technician contact?</legend>
          <Field id={`${id}-name`} label="Name" small>
            <input
              id={`${id}-name`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              autoComplete="name"
              maxLength={120}
              className={INPUT}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id={`${id}-phone`} label="Phone" small>
              <input
                id={`${id}-phone`}
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                autoComplete="tel"
                maxLength={40}
                className={INPUT}
              />
            </Field>
            <Field id={`${id}-email`} label="Email" small>
              <input
                id={`${id}-email`}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                maxLength={200}
                className={INPUT}
              />
            </Field>
          </div>
        </fieldset>
      </div>

      <div className="bg-card border-t px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {file.isError ? (
          <p role="alert" className="mb-2 text-sm text-rose-700">
            {file.error.message}
          </p>
        ) : null}
        <Button type="submit" className="h-11 w-full" disabled={!ready || file.isPending}>
          {file.isPending ? (
            <>
              <Loader2 aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
              Sending
            </>
          ) : (
            'Send claim'
          )}
        </Button>
      </div>
    </form>
  )
}

const INPUT = cn(
  'border-input bg-background h-11 w-full min-w-0 rounded-lg border px-3 text-[15px] outline-none',
  'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
)

function Field({
  id,
  label,
  optional = false,
  small = false,
  children,
}: {
  id: string
  label: string
  optional?: boolean
  small?: boolean
  children: ReactNode
}) {
  return (
    <div>
      <label htmlFor={id} className={cn('font-medium', small ? 'text-xs' : 'text-sm')}>
        {label}
        {optional ? <span className="text-muted-foreground font-normal"> (optional)</span> : null}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  )
}

function PhotoField({
  id,
  photos,
  onChange,
}: {
  id: string
  photos: File[]
  onChange: (photos: File[]) => void
}) {
  const urls = useMemo(() => photos.map((photo) => URL.createObjectURL(photo)), [photos])
  useEffect(() => () => urls.forEach((url) => URL.revokeObjectURL(url)), [urls])
  return (
    <div>
      <p id={`${id}-label`} className="text-sm font-medium">
        Photos of the problem and the rating plate
      </p>
      <ul aria-labelledby={`${id}-label`} className="mt-2 grid grid-cols-4 gap-2">
        {photos.map((photo, index) => (
          <li key={`${photo.name}-${index}`} className="relative">
            <img
              alt={photo.name}
              src={urls[index]}
              className="bg-muted aspect-square w-full rounded-lg border object-cover"
            />
            <button
              type="button"
              onClick={() => onChange(photos.filter((_, i) => i !== index))}
              className="bg-background/90 hover:bg-background focus-visible:ring-ring/50 absolute top-1 right-1 grid size-7 place-items-center rounded-full shadow-sm outline-none focus-visible:ring-[3px]"
            >
              <X aria-hidden="true" className="size-3.5" />
              <span className="sr-only">Remove {photo.name}</span>
            </button>
          </li>
        ))}
        {photos.length < MAX_PHOTOS ? (
          <li>
            <input
              id={id}
              type="file"
              accept="image/*"
              multiple
              className="peer sr-only"
              onChange={(event) => {
                const added = Array.from(event.target.files ?? [])
                onChange([...photos, ...added].slice(0, MAX_PHOTOS))
                event.target.value = ''
              }}
            />
            <label
              htmlFor={id}
              className="text-muted-foreground hover:text-foreground hover:bg-muted/60 peer-focus-visible:ring-ring/50 grid aspect-square w-full cursor-pointer place-items-center content-center gap-1 rounded-lg border border-dashed text-xs font-medium peer-focus-visible:ring-[3px]"
            >
              <Camera aria-hidden="true" className="size-5" />
              Add
              <span className="sr-only"> photos</span>
            </label>
          </li>
        ) : null}
      </ul>
    </div>
  )
}

function ItemImage({ url, small = false }: { url: string | null; small?: boolean }) {
  const [failed, setFailed] = useState(false)
  const size = small ? 'size-11' : 'size-14'
  if (!url || failed) {
    return (
      <span className={cn('bg-muted grid place-items-center rounded-lg', size)}>
        <Package aria-hidden="true" className="text-muted-foreground size-5" />
      </span>
    )
  }
  return (
    <img
      alt=""
      src={url}
      loading="lazy"
      onError={() => setFailed(true)}
      className={cn('rounded-lg border bg-white object-contain p-1', size)}
    />
  )
}
